"use client";

import { AnimatePresence, motion } from "motion/react";
import { useFileDetailModal } from "../../FileDetailModal/context/FileDetailModalContext";
import {
  ANALYZE_OVERLAY_PERSONA_GLOW,
  getPersonaOverlayColors,
  getScanPlaceholderPins,
} from "../analyzeImageOverlayUtils";
import type { ImageFrameRect } from "../hooks/useObjectContainImageFrame";
import { useAnalyzeReviewImageOverlay } from "../hooks/useAnalyzeReviewImageOverlay";
import { AnalyzeImageFrameLayer } from "./AnalyzeImageFrameLayer";
import { AnalyzeImageScanEffect } from "./AnalyzeImageScanEffect";

const AnalyzeImageScanLayer = ({ imageFrame }: { imageFrame: ImageFrameRect }) => {
  const { analyzeImageOverlay } = useFileDetailModal();
  const { feedFilter, generatingPersonaIds, scanPersonaId, scanPersonaName } = analyzeImageOverlay;

  if (generatingPersonaIds.length === 0) return null;

  const activePersonaId = scanPersonaId ?? feedFilter;
  const { glowColor, ringColor } = getPersonaOverlayColors(activePersonaId);
  const scanPins = scanPersonaId ? getScanPlaceholderPins(scanPersonaId) : [];

  return (
    <AnalyzeImageFrameLayer frame={imageFrame} zClassName="z-30">
      <AnimatePresence>
        <motion.div
          key="scan-layer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0"
        >
          <AnalyzeImageScanEffect
            ringColor={ringColor}
            glowColor={glowColor}
            scanPersonaName={scanPersonaName}
            placeholderPins={scanPins}
          />
        </motion.div>
      </AnimatePresence>
    </AnalyzeImageFrameLayer>
  );
};

/** Soft advisor-colored inset glow when an advisor filter is active (not while scanning). */
const AnalyzeImageAdvisorGlowLayer = ({ imageFrame }: { imageFrame: ImageFrameRect }) => {
  const { analyzeImageOverlay } = useFileDetailModal();
  const { feedFilter, generatingPersonaIds } = analyzeImageOverlay;

  if (!feedFilter || generatingPersonaIds.length > 0) return null;

  const { glowColor } = getPersonaOverlayColors(feedFilter);

  return (
    <AnalyzeImageFrameLayer frame={imageFrame} zClassName="z-20">
      <div
        className="absolute inset-0 rounded-md pointer-events-none transition-opacity"
        style={{
          boxShadow: `inset 0 0 40px 6px ${ANALYZE_OVERLAY_PERSONA_GLOW[feedFilter] ?? glowColor}`,
        }}
      />
    </AnalyzeImageFrameLayer>
  );
};

/* V2: re-enable numbered pins when backend provides insight coordinates on the image.
const AnalyzeImagePinsLayer = ({ imageFrame }: { imageFrame: ImageFrameRect }) => {
  const { analyzeImageOverlay, setAnalyzeImageOverlayHighlight } = useFileDetailModal();
  const { callouts, feedFilter, highlightedMessageId, highlightedCalloutId } = analyzeImageOverlay;
  const visibleCallouts = callouts.filter((c) => !feedFilter || c.personaId === feedFilter);
  const { glowColor } = getPersonaOverlayColors(feedFilter);

  return (
    <AnalyzeImageFrameLayer frame={imageFrame} zClassName="z-25">
      <AnimatePresence>
        {visibleCallouts.map((callout) => {
          const isHighlighted =
            highlightedMessageId === callout.messageId ||
            highlightedCalloutId === callout.id;
          const pinRing =
            ANALYZE_OVERLAY_PERSONA_RING[callout.personaId] ?? "hsl(var(--primary))";

          return (
            <motion.div
              key={callout.id}
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.6 }}
              className="pointer-events-auto absolute -translate-x-1/2 -translate-y-1/2"
              style={{ top: `${callout.top}%`, left: `${callout.left}%` }}
            >
              {isHighlighted && (
                <motion.span
                  className="absolute left-1/2 top-1/2 -z-10 h-28 w-28 -translate-x-1/2 -translate-y-1/2 rounded-full"
                  style={{
                    background: `radial-gradient(circle, ${ANALYZE_OVERLAY_PERSONA_GLOW[callout.personaId] ?? glowColor} 0%, transparent 70%)`,
                  }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                />
              )}
              <button
                type="button"
                title={callout.label}
                onClick={() => {
                  setAnalyzeImageOverlayHighlight(callout.messageId, callout.id);
                  scrollToAnalyzeInsight(callout.messageId);
                }}
                onMouseEnter={() =>
                  setAnalyzeImageOverlayHighlight(callout.messageId, callout.id)
                }
                onMouseLeave={() => setAnalyzeImageOverlayHighlight(null, null)}
                onFocus={() =>
                  setAnalyzeImageOverlayHighlight(callout.messageId, callout.id)
                }
                onBlur={() => setAnalyzeImageOverlayHighlight(null, null)}
                className={cn(
                  "flex h-7 min-w-[1.75rem] items-center justify-center rounded-full border-2 border-white/90 text-[11px] font-bold text-white shadow-lg transition-transform",
                  isHighlighted && "scale-110 ring-2 ring-white/50",
                )}
                style={{ background: pinRing }}
              >
                {callout.index}
              </button>
              {isHighlighted && (
                <motion.div
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute left-1/2 top-full z-10 mt-1.5 w-max max-w-[220px] -translate-x-1/2 rounded-md border border-white/15 bg-black/80 px-2.5 py-1.5 text-[10px] leading-snug text-white backdrop-blur-sm pointer-events-none"
                >
                  {callout.label}
                </motion.div>
              )}
            </motion.div>
          );
        })}
      </AnimatePresence>
    </AnalyzeImageFrameLayer>
  );
};
*/

/** Scan + advisor glow aligned to the visible image while Analyze is open on Review. */
export const AnalyzeImageLayers = ({ imageFrame }: { imageFrame: ImageFrameRect | null }) => {
  const { isActive, imageFrame: frame } = useAnalyzeReviewImageOverlay(imageFrame);
  if (!isActive || !frame) return null;

  return (
    <>
      <AnalyzeImageAdvisorGlowLayer imageFrame={frame} />
      <AnalyzeImageScanLayer imageFrame={frame} />
      {/* <AnalyzeImagePinsLayer imageFrame={frame} /> */}
    </>
  );
};
